// src/pages/LancamentoFinanceiro/LancamentoFinanceiroForm.jsx

import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import Header from '../../components/Header';
import CurrencyInput from '../../components/CurrencyInput';
import api from '../../services/api';
import { showSuccess, showError } from '../../services/alert';
import { useModules } from '../../contexts/ModuleContext';
import './LancamentoFinanceiroForm.css';

const LancamentoFinanceiroForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { modules } = useModules();

  const isEditing = Boolean(id && id !== 'novo');
  const [tipo, setTipo] = useState(searchParams.get('tipo') === 'receita' ? 'receita' : 'despesa');
  const isReceita = isEditing ? location.pathname.startsWith('/receita') : tipo === 'receita';
  const tipoLancamento = isReceita ? 'Receita' : 'Despesa';
  const permiteVincularSafra = ['producao', 'estoque', 'gestao', 'lia'].some(modulo => modules.includes(modulo));
  const hoje = new Date().toISOString().substring(0, 10);

  const [lancamento, setLancamento] = useState({
    valor: '',
    dataLancamento: hoje,
    dataVencimento: hoje,
    formaPagamento: '5',
    statusPagamento: '0',
    recorrente: false,
    centroCustoId: '',
    descricao: '',
    categoriaFinanceiraId: '',
    safraId: ''
  });

  const [categorias, setCategorias] = useState([]);
  const [safras, setSafras] = useState([]);
  const [loading, setLoading] = useState(false);
  const [novaCategoriaAberta, setNovaCategoriaAberta] = useState(false);
  const [novaCategoriaNome, setNovaCategoriaNome] = useState('');
  const [salvandoCategoria, setSalvandoCategoria] = useState(false);

  useEffect(() => {
    const fetchCategorias = async () => {
      try {
        const res = await api.get('/categoriafinanceira');
        setCategorias(res.data.filter(c => c.tipo === tipoLancamento));
      } catch {
        showError('Erro ao carregar categorias financeiras.');
      }
    };
    fetchCategorias();
  }, [tipoLancamento]);

  useEffect(() => {
    if (!permiteVincularSafra) {
      setSafras([]);
      setLancamento(prev => ({ ...prev, safraId: '' }));
      return;
    }

    const fetchSafras = async () => {
      try {
        const res = await api.get('/safras');
        setSafras(res.data);
      } catch {
        showError('Erro ao carregar safras.');
      }
    };

    fetchSafras();
  }, [permiteVincularSafra]);

  useEffect(() => {
    if (!isEditing) return;

    setLoading(true);
    const endpoint = isReceita ? `/receita/${id}` : `/despesa/${id}`;

    api.get(endpoint)
      .then(response => {
        const d = response.data;
        setLancamento({
          valor: d.valor.toString(),
          dataLancamento: (isReceita ? d.dataRecebimento : d.dataPagamento)?.substring(0, 10) || '',
          dataVencimento: d.dataVencimento?.substring(0, 10) || '',
          formaPagamento: d.formaPagamento?.toString() || '',
          statusPagamento: d.statusPagamento?.toString() || '',
          recorrente: d.recorrente || false,
          centroCustoId: d.centroCustoId?.toString() || '',
          descricao: d.descricao || '',
          safraId: d.safraId?.toString() || '',
          categoriaFinanceiraId: d.categoriaFinanceiraId?.toString() || ''
        });
      })
      .catch(() => {
        showError(`Erro ao carregar ${tipoLancamento.toLowerCase()}.`);
      })
      .finally(() => setLoading(false));
  }, [isEditing, id, isReceita, tipoLancamento]);

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setLancamento(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const alterarTipo = novoTipo => {
    setTipo(novoTipo);
    setLancamento(prev => ({ ...prev, categoriaFinanceiraId: '' }));
    setNovaCategoriaAberta(false);
    setNovaCategoriaNome('');
  };

  const criarCategoriaRapida = async () => {
    const nome = novaCategoriaNome.trim();
    if (!nome) {
      showError('Informe o nome da nova categoria.');
      return;
    }

    const existente = categorias.find(c => c.nome.trim().toLocaleLowerCase('pt-BR') === nome.toLocaleLowerCase('pt-BR'));
    if (existente) {
      setLancamento(prev => ({ ...prev, categoriaFinanceiraId: String(existente.id) }));
      setNovaCategoriaAberta(false);
      setNovaCategoriaNome('');
      showSuccess('Essa categoria já existia e foi selecionada.');
      return;
    }

    setSalvandoCategoria(true);
    try {
      const response = await api.post('/categoriafinanceira', {
        id: 0,
        nome,
        tipo: tipoLancamento
      });
      const criada = response.data;
      setCategorias(prev => [...prev, criada].sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR')));
      setLancamento(prev => ({ ...prev, categoriaFinanceiraId: String(criada.id) }));
      setNovaCategoriaAberta(false);
      setNovaCategoriaNome('');
      showSuccess(`Categoria de ${tipoLancamento.toLowerCase()} criada e selecionada.`);
    } catch (err) {
      showError(err.response?.data?.message || 'Erro ao criar a categoria financeira.');
    } finally {
      setSalvandoCategoria(false);
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (!lancamento.valor || !lancamento.dataLancamento || !lancamento.descricao || !lancamento.categoriaFinanceiraId) {
      showError('Preencha todos os campos obrigatórios.');
      return;
    }

    const categoriaSelecionada = categorias.find(c => c.id === Number(lancamento.categoriaFinanceiraId));
    if (!categoriaSelecionada) {
      showError('Categoria Financeira inválida.');
      return;
    }

    const payloadBase = {
      id: isEditing ? Number(id) : 0,
      valor: parseFloat(lancamento.valor),
      descricao: lancamento.descricao.trim(),
      dataVencimento: lancamento.dataVencimento ? new Date(lancamento.dataVencimento).toISOString() : null,
      formaPagamento: Number(lancamento.formaPagamento),
      statusPagamento: Number(lancamento.statusPagamento),
      recorrente: lancamento.recorrente,
      centroCustoId: Number(lancamento.centroCustoId) || null,
      safraId: Number(lancamento.safraId) || null,
      categoriaFinanceiraId: Number(lancamento.categoriaFinanceiraId),
      categoriaFinanceira: {
        id: Number(lancamento.categoriaFinanceiraId),
        nome: categoriaSelecionada.nome,
        tipo: categoriaSelecionada.tipo
      }
    };

    const payload = isReceita
      ? { ...payloadBase, dataRecebimento: new Date(lancamento.dataLancamento).toISOString() }
      : { ...payloadBase, dataPagamento: new Date(lancamento.dataLancamento).toISOString() };

    setLoading(true);
    try {
      if (isEditing) {
        await api.put(`${isReceita ? '/receita' : '/despesa'}/${id}`, payload);
        showSuccess(`${tipoLancamento} atualizada com sucesso.`);
      } else {
        await api.post(isReceita ? '/receita' : '/despesa', payload);
        showSuccess(`${tipoLancamento} criada com sucesso.`);
      }
      navigate('/contasPagarReceber');
    } catch (err) {
      showError(err.response?.data?.message || `Erro ao salvar ${tipoLancamento.toLowerCase()}.`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mt-4 financeiro-form-page">
      <Header />
      <div className="card shadow-lg border-0 rounded-3">
        <div className="card-header bg-success text-white d-flex align-items-center gap-3 rounded-top-3">
          <button className="btn btn-light btn-sm" onClick={() => navigate('/contasPagarReceber')} disabled={loading}>
            <i className="bi bi-arrow-left text-success" /> Voltar
          </button>
          <div><h5 className="mb-0">{isEditing ? `Editar ${tipoLancamento}` : 'Novo lançamento'}</h5><small className="text-white-50">Preencha somente as informações essenciais</small></div>
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmit} className="row g-4">
            <fieldset className="border p-3 rounded-3 row g-3 mx-0">
              <legend className="px-3 text-success fw-semibold">Dados do lançamento</legend>

              {!isEditing && <div className="col-12 mb-4"><label className="form-label fw-semibold d-block">O que você quer lançar?</label><div className="btn-group w-100" role="group" aria-label="Tipo do lançamento"><button type="button" className={`btn py-3 ${!isReceita ? 'btn-danger' : 'btn-outline-danger'}`} onClick={() => alterarTipo('despesa')}><i className="bi bi-arrow-up-circle me-2" />Despesa</button><button type="button" className={`btn py-3 ${isReceita ? 'btn-success' : 'btn-outline-success'}`} onClick={() => alterarTipo('receita')}><i className="bi bi-arrow-down-circle me-2" />Receita</button></div></div>}

              <div className="col-12">
                <div className="categoria-financeira-field">
                  <div className="d-flex align-items-center justify-content-between gap-3 mb-2">
                    <label className="form-label fw-semibold mb-0" htmlFor="categoriaFinanceiraId">Categoria financeira</label>
                    <span className={`categoria-tipo-badge ${isReceita ? 'receita' : 'despesa'}`}>{tipoLancamento}</span>
                  </div>
                  <div className="categoria-select-row">
                    <div className="categoria-select-wrap">
                      <i className="bi bi-tags categoria-select-icon" aria-hidden="true" />
                      <select id="categoriaFinanceiraId" name="categoriaFinanceiraId" value={lancamento.categoriaFinanceiraId} onChange={handleChange} className="form-select rounded-3" required autoFocus>
                        <option value="">Selecione uma categoria</option>
                        {categorias.map(c => (
                          <option key={c.id} value={c.id}>{c.nome}</option>
                        ))}
                      </select>
                    </div>
                    <button
                      type="button"
                      className={`btn categoria-add-button ${novaCategoriaAberta ? 'btn-secondary' : 'btn-outline-success'}`}
                      onClick={() => setNovaCategoriaAberta(aberta => !aberta)}
                      aria-expanded={novaCategoriaAberta}
                    >
                      <i className={`bi ${novaCategoriaAberta ? 'bi-x-lg' : 'bi-plus-lg'}`} />
                      <span>{novaCategoriaAberta ? 'Cancelar' : 'Nova categoria'}</span>
                    </button>
                  </div>
                  {!novaCategoriaAberta && <small className="categoria-helper">Não encontrou a categoria? Cadastre uma nova sem sair do lançamento.</small>}

                  {novaCategoriaAberta && (
                  <div className="categoria-quick-create mt-3">
                    <label htmlFor="novaCategoriaNome" className="form-label fw-semibold mb-1">
                      Nova categoria de {tipoLancamento.toLowerCase()}
                    </label>
                    <div className="input-group">
                      <input
                        id="novaCategoriaNome"
                        type="text"
                        className="form-control"
                        value={novaCategoriaNome}
                        onChange={e => setNovaCategoriaNome(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            criarCategoriaRapida();
                          }
                        }}
                        placeholder={isReceita ? 'Ex.: Venda de grãos' : 'Ex.: Manutenção de máquinas'}
                        maxLength={100}
                        autoFocus
                        disabled={salvandoCategoria}
                      />
                      <button type="button" className="btn btn-success px-4" onClick={criarCategoriaRapida} disabled={salvandoCategoria}>
                        <i className="bi bi-check-lg me-2" />
                        {salvandoCategoria ? 'Criando...' : 'Criar e selecionar'}
                      </button>
                    </div>
                    <small className="text-muted">Ela será salva como {tipoLancamento.toLowerCase()} e selecionada neste lançamento.</small>
                  </div>
                  )}
                </div>
              </div>

              <div className="col-md-4">
                <label className="form-label fw-semibold">Valor (R$)</label>
                <CurrencyInput name="valor" value={lancamento.valor} onChange={handleChange} className="form-control rounded-3" placeholder="0,00" required />
              </div>

              <div className="col-md-4">
                <label className="form-label fw-semibold">Data do lançamento</label>
                <input type="date" name="dataLancamento" value={lancamento.dataLancamento} onChange={handleChange} className="form-control rounded-3" required />
              </div>

              <div className="col-md-4">
                <label className="form-label fw-semibold">Data de Vencimento</label>
                <input type="date" name="dataVencimento" value={lancamento.dataVencimento} onChange={handleChange} className="form-control rounded-3" />
              </div>

              <div className="col-md-4">
                <label className="form-label fw-semibold">Forma de Pagamento</label>
                <select name="formaPagamento" value={lancamento.formaPagamento} onChange={handleChange} className="form-select rounded-3">
                  <option value="0">Dinheiro</option>
                  <option value="1">Pix</option>
                  <option value="2">Cartão de crédito</option>
                  <option value="3">Cartão de débito</option>
                  <option value="4">Transferência</option>
                  <option value="5">Boleto</option>
                </select>
              </div>

              <div className="col-md-4 d-none">
                <label className="form-label fw-semibold">Centro de Custo</label>
                <input type="number" name="centroCustoId" value={lancamento.centroCustoId} onChange={handleChange} className="form-control rounded-3" />
              </div>

              <div className="col-md-4 d-flex align-items-center">
                <div className="form-check mt-4">
                  <input type="checkbox" name="recorrente" checked={lancamento.recorrente} onChange={handleChange} className="form-check-input" />
                  <label className="form-check-label"><b>Repetir todo mês</b><small className="d-block text-muted">O sistema criará este lançamento automaticamente.</small></label>
                </div>
              </div>
              {permiteVincularSafra && <div className="col-md-6">
                <label className="form-label fw-semibold">Safra</label>
                <select
                  name="safraId"
                  value={lancamento.safraId}
                  onChange={handleChange}
                  className="form-select rounded-3"
                >
                  <option value="">Não vincular à safra</option>
                  {safras.map(s => (
                    <option key={s.id} value={s.id}>
                      {s.cultura} - Plantio: {s.dataPlantio ? new Date(s.dataPlantio).toLocaleDateString() : ''}
                    </option>
                  ))}
                </select>
              </div>}
              <div className={permiteVincularSafra ? 'col-md-6' : 'col-12'}>
                <label className="form-label fw-semibold">Descrição</label>
                <input type="text" name="descricao" value={lancamento.descricao} onChange={handleChange} className="form-control rounded-3" maxLength={200} required />
              </div>

              <div className="col-12 d-flex justify-content-end mt-3">
                <button type="submit" className="btn btn-success me-2 d-flex align-items-center gap-1" disabled={loading}>
                  <i className="bi bi-save" />
                  <span>{loading ? 'Salvando...' : 'Salvar'}</span>
                </button>
                <button type="button" className="btn btn-secondary d-flex align-items-center gap-1" onClick={() => navigate('/contasPagarReceber')} disabled={loading}>
                  <i className="bi bi-x-circle" />
                  <span>Cancelar</span>
                </button>
              </div>
            </fieldset>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LancamentoFinanceiroForm;
